import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { fetchNationwideSitesCount } from 'lib/utils/coveo/sitesHeadless/fetchNationwideSitesCount';
import isClinicalTrialSite from 'lib/utils/siteHelper';
import { setNationwideTotal } from 'src/features/locationSearchSlice';
import { RootState } from 'src/store/store';

type PersistPartial = {
  _persist?: { rehydrated: boolean };
};

type PersistRootState = RootState & PersistPartial;

function isNationwideCountRehydrated(state: PersistRootState): boolean {
  if (state._persist?.rehydrated !== true) {
    return false;
  }

  const locationPersist = (state.locationSearch as PersistPartial)._persist;
  return locationPersist?.rehydrated === true;
}

function useNationwideSitesCount(): void {
  const dispatch = useDispatch();
  const nationwideTotal = useSelector((state: RootState) => state.locationSearch.nationwideTotal);
  const rehydrated = useSelector((state: PersistRootState) => isNationwideCountRehydrated(state));
  const { sitecoreContext } = useSitecoreContext();
  const siteName = sitecoreContext?.site?.name;

  useEffect(() => {
    const isFindSiteEnabled =
      process.env.NEXT_PUBLIC_ENABLE_FIND_SITE_FUNCTIONALITY?.toLowerCase() === 'true';
    if (!siteName || !isClinicalTrialSite(siteName as string) || !isFindSiteEnabled) {
      return;
    }
    if (!rehydrated) {
      return;
    }
    if (nationwideTotal > 0) {
      return;
    }
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }

    let cancelled = false;

    void fetchNationwideSitesCount()
      .then((total) => {
        if (cancelled || total <= 0) {
          return;
        }
        dispatch(setNationwideTotal(total));
      })
      .catch(() => {
        // Leave nationwideTotal at 0 so a later mount can retry.
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, nationwideTotal, rehydrated, siteName, sitecoreContext?.pageState]);
}

export default useNationwideSitesCount;
export { useNationwideSitesCount };
